import { useMutation, useQuery } from "react-query";
import { t } from "@/i18n-js/instance";
import type { ApiError } from "@/react/config/CustomErrors";
import {
  reactQueryGet,
  reactQueryPut,
} from "@/react/helpers/backendRequestHelpers";
import { internalApi } from "@/react/helpers/urlHelpers";
import { useToast } from "@circle-react-shared/uikit/ToastV2";
import type { SiteTheme } from "../../shared/BuilderUI/SiteThemes/constants";

export const DESIGN_CONFIG_QUERY_KEY = "design-config" as const;

interface ImageField {
  signed_id: string;
  url: string;
}

interface BrandedMobileApp {
  brand_color_dark_mode: string;
  brand_color_light_mode: string;
  button_text_color_dark: string;
  button_text_color_light: string;
  can_register?: boolean;
  community_icon_dark?: string;
  community_icon_light?: string;
  community_logo_dark?: string;
  community_logo_light?: string;
  custom_signup_url?: string;
  terms_of_service_url?: string;
}

interface BrandedMobileAppRequest {
  branded_mobile_app: BrandedMobileApp;
}

export interface BrandedMobileAppResponse {
  attachments: any[];
  config: {
    can_register: boolean;
    terms_of_service_url: string;
    custom_signup_url: string;
    meta_tracking_enabled: boolean;
    community_logo_light: ImageField | null;
    community_logo_dark: ImageField | null;
    community_icon_light: ImageField | null;
    community_icon_dark: ImageField | null;
  };
  theme: {
    brand_color_light_mode: string;
    brand_color_dark_mode: string;
    button_text_color_light: string;
    button_text_color_dark: string;
  };
}

export const useDesignConfigData = () => {
  const toast = useToast();

  const { data, isLoading } = useQuery<
    unknown,
    ApiError,
    BrandedMobileAppResponse
  >(DESIGN_CONFIG_QUERY_KEY, () =>
    reactQueryGet(internalApi.brandedAppsBuilderUI.designConfig.get()),
  );

  const { mutateAsync: mutateDesignConfig } = useMutation<
    void,
    ApiError,
    BrandedMobileAppRequest
  >({
    mutationFn: (designConfig: BrandedMobileAppRequest) =>
      reactQueryPut(
        internalApi.brandedAppsBuilderUI.designConfig.put(),
        designConfig,
      ),
    onSuccess: () => {
      toast.success(t("branded_apps_builder_ui.design.save.success"));
    },
    onError: (error: { message: string }) => {
      toast.error(t("branded_apps_builder_ui.design.save.error"));
      console.error(error);
    },
  });

  const mapThemeToFormData = (theme: SiteTheme) => ({
    brand_color_light_mode: theme?.cssVariables?.light?.brand,
    brand_color_dark_mode: theme?.cssVariables?.dark?.brand,
    button_text_color_light: theme?.cssVariables?.light?.["button-text-color"],
    button_text_color_dark: theme?.cssVariables?.dark?.["button-text-color"],
    community_icon_light:
      theme?.cssVariables?.light?.["community-icon-signed-id"],
    community_logo_light:
      theme?.cssVariables?.light?.["community-logo-signed-id"],
    community_icon_dark:
      theme?.cssVariables?.dark?.["community-icon-signed-id"],
    community_logo_dark:
      theme?.cssVariables?.dark?.["community-logo-signed-id"],
  });

  const saveDesignConfig = async (theme: SiteTheme) =>
    mutateDesignConfig({ branded_mobile_app: mapThemeToFormData(theme) });

  return {
    designConfigData: data,
    mutateDesignConfig,
    isLoading,
    saveDesignConfig,
  };
};
